import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, FlaskConical } from 'lucide-react';
import { ROUTES } from '../../constants';
import { loadRecentPapers, type NewsPaperDoc } from '../../lib/newsPapers';
import { PaperCard } from './PaperCard';

const FIELD_COLORS: Record<string, string> = {
  'AI / ML':      'bg-violet-500/15 text-violet-400 ring-violet-500/25',
  'Physics':      'bg-blue-500/15 text-blue-400 ring-blue-500/25',
  'Mathematics':  'bg-amber-500/15 text-amber-400 ring-amber-500/25',
  'Biology':      'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
  'Neuroscience': 'bg-pink-500/15 text-pink-400 ring-pink-500/25',
  'Economics':    'bg-orange-500/15 text-orange-400 ring-orange-500/25',
  'Statistics':   'bg-cyan-500/15 text-cyan-400 ring-cyan-500/25',
};

function fieldBadgeClass(field: string) {
  return FIELD_COLORS[field] ?? 'bg-surface-raised text-fg-muted ring-border';
}

function dateRange(days: number): string {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days + 1);
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
}

function groupByField(papers: NewsPaperDoc[]): [string, NewsPaperDoc[]][] {
  const map = new Map<string, NewsPaperDoc[]>();
  for (const p of papers) {
    const arr = map.get(p.researchArea) ?? [];
    arr.push(p);
    map.set(p.researchArea, arr);
  }
  return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
}

export function NewspaperSection() {
  const [papers, setPapers] = useState<NewsPaperDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const rows = await loadRecentPapers(7);
      if (!cancelled) {
        setPapers(rows);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hero = papers[0] ?? null;
  const fieldSections = useMemo(() => groupByField(papers.slice(1)), [papers]);
  const uniqueFieldCount = useMemo(
    () => new Set(papers.map(p => p.researchArea)).size,
    [papers]
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-52 animate-pulse rounded bg-surface-raised" />
        <div className="h-56 animate-pulse rounded-card border border-border bg-surface-raised/40" />
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 animate-pulse rounded-card border border-border bg-surface-raised/40" />
        ))}
      </div>
    );
  }

  if (papers.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-card border border-border bg-surface-card py-16 text-center">
        <BookOpen size={32} strokeWidth={1} className="text-fg-subtle" />
        <p className="text-sm text-fg-muted">
          No papers from the last 7 days.{' '}
          <Link to={ROUTES.arxivTest} className="text-brand hover:underline">
            Fetch papers
          </Link>{' '}
          first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Masthead */}
      <div className="border-b-2 border-fg pb-3">
        <h2 className="font-display text-2xl font-bold tracking-tight text-fg">
          The Erudis Weekly
        </h2>
        <div className="mt-1 flex items-center justify-between text-xs text-fg-muted">
          <span>{dateRange(7)}</span>
          <span>
            {papers.length} papers · {uniqueFieldCount} field{uniqueFieldCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Hero — top scoring paper of the week */}
      {hero && <PaperCard paper={hero} />}

      {/* Per-field sections, sorted by paper count */}
      {fieldSections.map(([field, fieldPapers]) => (
        <section key={field}>
          <div className="mb-2 flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${fieldBadgeClass(field)}`}
            >
              <FlaskConical size={10} strokeWidth={2} />
              {field}
            </span>
            <div className="flex-1 border-t border-border" />
            <span className="text-[10px] text-fg-subtle">{fieldPapers.length} papers</span>
          </div>
          <ul>
            {fieldPapers.slice(0, 6).map(p => (
              <PaperCard key={p.id} paper={p} compact />
            ))}
            {fieldPapers.length > 6 && (
              <li className="pt-2 text-[11px] text-fg-subtle">
                +{fieldPapers.length - 6} more in this field
              </li>
            )}
          </ul>
        </section>
      ))}

      <p className="text-center text-[10px] italic text-fg-subtle">
        "Thank you to arXiv for use of its open access interoperability."
      </p>
    </div>
  );
}
